import { memo } from 'react';
import { kebabCase } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography, SwitchProps } from '@mui/material';
import {
  Agreement,
  AgreementSelectionType,
  AgreementDocument,
} from 'api/models/agreement';
import { TableDocument as DocumentIcon } from 'iconsax-react';
import { Checkbox } from 'components/Checkbox';
import { ErrorMessage } from 'components/ErrorMessage';
import { Tooltip } from 'components/Tooltip';
import { ConsentWrapper } from './styled';
import { messages } from '../../messages';

interface Props {
  agreement: Agreement;
  onOpenDocument(document: AgreementDocument): void;
  checked: boolean;
  onCheck: SwitchProps['onChange'];
  error?: string | false;
  disabled?: string | false;
}

export const Consent = memo(
  ({
    agreement: { title, selectionType, document },
    onOpenDocument,
    onCheck,
    checked,
    error,
    disabled,
  }: Props) => {
    const { t } = useTranslation();
    const checkboxId = `consent-checkbox-${kebabCase(title)}`;

    const getCheckbox = () => {
      if (disabled) {
        return (
          <Tooltip title={disabled}>
            <span>
              <Checkbox
                id={checkboxId}
                data-cy={checkboxId}
                checked={checked}
                onChange={onCheck}
                disabled
              />
            </span>
          </Tooltip>
        );
      }

      return (
        <Checkbox
          id={checkboxId}
          data-cy={checkboxId}
          checked={checked}
          onChange={onCheck}
        />
      );
    };

    const isAgreementOptional =
      selectionType === AgreementSelectionType.OPTIONAL_CHANGE_ALLOWED ||
      (selectionType ===
        AgreementSelectionType.OPTIONAL_ONLY_CHANGE_AGREE_ALLOWED &&
        !disabled);

    return (
      <div>
        <ConsentWrapper invalid={!!error}>
          {getCheckbox()}
          <Stack gap=".4rem" overflow="clip" width="100%">
            <label htmlFor={checkboxId}>
              <Typography variant="h5" component="span">
                {title}
              </Typography>
              {isAgreementOptional && (
                <Typography
                  variant="h5"
                  color="text.secondary"
                  component="span"
                >
                  {` (${t(messages.optional())})`}
                </Typography>
              )}
            </label>
            {document !== undefined && (
              <Button
                variant="text"
                style={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  width: 'fit-content',
                  maxWidth: 'calc(100% - 2.4rem)',
                  textOverflow: 'ellipsis',
                }}
                onClick={() => onOpenDocument(document)}
              >
                <DocumentIcon
                  size="1.6rem"
                  style={{ marginBottom: '.25rem' }}
                />
                {document.title}
              </Button>
            )}
          </Stack>
        </ConsentWrapper>
        <ErrorMessage message={error || undefined} />
      </div>
    );
  },
);
